import React, { useEffect, useState } from 'react';

const GUTTER_ATTR = 'data-line-gutter';

// Visual text lines inside `container`, as [{ top, height }] relative to it. Each
// rendered line box of every text node is measured, so wrapped lines are numbered
// exactly as displayed; boxes that overlap vertically belong to the same line.
const measureLines = (container) => {
  const origin = container.getBoundingClientRect();
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (
      !node.textContent.trim() || node.parentElement?.closest(`[${GUTTER_ATTR}]`)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT
    ),
  });
  const boxes = [];
  const range = document.createRange();
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    range.selectNodeContents(node);
    Array.from(range.getClientRects()).forEach((r) => {
      if (r.height > 0 && r.width > 0) boxes.push({ top: r.top - origin.top, bottom: r.bottom - origin.top });
    });
  }
  boxes.sort((a, b) => a.top - b.top);
  const lines = [];
  boxes.forEach((box) => {
    const last = lines[lines.length - 1];
    if (last && box.top < last.bottom - 2) last.bottom = Math.max(last.bottom, box.bottom);
    else lines.push({ ...box });
  });
  return lines.map((l) => ({ top: l.top, height: l.bottom - l.top }));
};

/**
 * Continuous line numbers in the left margin of `containerRef` (which must be
 * position: relative and leave room on the left). Re-measures on resize and on
 * content changes.
 */
const LineNumberGutter = ({ containerRef, width = 40 }) => {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    let frame = null;
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setLines(measureLines(container)));
    };
    schedule();
    const resize = new ResizeObserver(schedule);
    resize.observe(container);
    const mutations = new MutationObserver((records) => {
      if (records.some((r) => !(r.target instanceof Element && r.target.closest(`[${GUTTER_ATTR}]`)))) schedule();
    });
    mutations.observe(container, { childList: true, subtree: true, characterData: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      resize.disconnect();
      mutations.disconnect();
    };
  }, [containerRef]);

  return (
    <div
      {...{ [GUTTER_ATTR]: true }}
      aria-hidden="true"
      style={{ position: 'absolute', left: 0, top: 0, width, height: '100%', pointerEvents: 'none', userSelect: 'none' }}
    >
      {lines.map((line, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{
            position: 'absolute', right: 8, top: line.top, height: line.height,
            display: 'flex', alignItems: 'center',
            fontSize: 11, color: '#9e9e9e', fontVariantNumeric: 'tabular-nums',
          }}
        >
          {i + 1}
        </span>
      ))}
    </div>
  );
};

export default LineNumberGutter;
